import { FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { COMMON_CONTAINER_ID } from '@/config';
import {
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
} from '@/utils';
import { InvoiceRequest } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentSelectors, userSelectors } from '@/redux/selectors';
import { paymentActions } from '@/redux/actions';
import { Button } from '@/components';

import style from './CardCheckoutForm.module.sass';

type CheckoutFormProps = {
  planId: number;
  tariffType?: string;
};

const CardCheckoutForm = ({ planId, tariffType }: CheckoutFormProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { stripeKey, paymentLoading, paymentPlansError } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);

  useEffect(() => {
    if (paymentPlansError) {
      toast.error(paymentPlansError, { containerId: COMMON_CONTAINER_ID });
    }
    return () => {
      dispatch(paymentActions.setError(null));
    };
  }, [paymentPlansError]);

  useEffect(() => {
    if (!stripeKey || !stripe || !elements) return;

    const doConfirm = async () => {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: stripeKey,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: userData?.fullName,
              email: userData?.emailAddress,
              phone: '',
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(`${error.message} ${t('translation.try_again_msg')}`, {
          containerId: COMMON_CONTAINER_ID,
        });
      } else {
        toast.success(t('translation.card_deposit_success'), {
          containerId: COMMON_CONTAINER_ID,
        });
      }

      dispatch(paymentActions.setPaymentLoading(false));
      dispatch(paymentActions.setStripeKey(null));
    };

    doConfirm();
  }, [stripeKey]);

  const performSubmit = async () => {
    if (!stripe || !elements) return;

    const { error: submitError } = await elements.submit();
    if (submitError) {
      toast.error(submitError.message, { containerId: COMMON_CONTAINER_ID });
      return;
    }

    dispatch(paymentActions.setPaymentLoading(true));

    let gClientId;
    let gSessionId;
    try {
      gClientId = await getGoogleAnalyticsClientId();
      gSessionId = await getGoogleAnalyticsSessionId();
    } catch (e) {
      console.log(e);
    }

    const payload: InvoiceRequest = {
      plan: planId,
      asset: 'USD',
      type: 'plan',
      gClientId,
      gSessionId,
    };

    dispatch(paymentActions.createStripeInvoice(payload));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    performSubmit();
  };

  return (
    <form className={style.form}>
      <PaymentElement
        className={style.card}
        options={{
          readOnly: paymentLoading,
          fields: {
            billingDetails: {
              name: 'never',
              email: 'never',
              phone: 'never',
            },
          },
          wallets: {
            googlePay: 'never',
            applePay: 'never',
            link: 'never',
          },
        }}
      />
      <Button
        isLoading={paymentLoading}
        className={style.btn}
        disabled={!stripe || paymentLoading}
        onClick={handleSubmit}
      >
        {t('translation.pay_btn')}
      </Button>
    </form>
  );
};

export default CardCheckoutForm;
